import clsx from 'clsx'
import React, { useState } from 'react'
import { Button } from 'react-aria-components'
import { useTranslation } from 'react-i18next'

import { Icon } from '#components/Icon'
import { useSidebar } from '#hooks/useSidebar'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

const STORAGE_PREFIX = 'sidenav-'

export const ExpandableNavItem = ({ label, icon, storageKey, badge, children }: IProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`)
    return stored === null ? true : stored === 'true'
  })

  const { t } = useTranslation()
  const { isCollapsed } = useSidebar()

  const handleToggle = (): void => {
    setIsOpen((prev) => {
      const next = !prev
      localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, String(next))
      return next
    })
  }

  return (
    <div className={clsx(styles.expandableItem, isCollapsed && styles.collapsed)}>
      <Button
        className={styles.trigger}
        onPress={handleToggle}
        aria-expanded={isOpen}
        aria-label={t('Toggle {{ label }}', { label })}
        data-testid={`expandable-${storageKey}`}
      >
        {/* The expanded sidebar has no icons (DS); the collapsed rail shows only the icon */}
        {icon && isCollapsed && (
          <span className={styles.iconBox}>
            <Icon name={icon} color="currentColor" width="20" height="20" />
          </span>
        )}
        <span className={styles.label}>{label}</span>
        {badge && !isCollapsed && (
          <span className={styles.badge} data-testid={`expandable-${storageKey}-badge`}>
            {badge}
          </span>
        )}
        <Icon
          name="chevron-left"
          width="14"
          height="14"
          className={clsx(styles.chevron, isOpen && styles.chevronOpen)}
        />
      </Button>
      {!isCollapsed && (
        <div className={clsx(styles.childrenWrapper, isOpen && styles.childrenOpen)}>
          <div className={styles.children}>{children}</div>
        </div>
      )}
    </div>
  )
}
